import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { loadPlayerJs } from '@/hooks/bunnyPlayerJs';

/** Fallback, falls das 'load'-Event des iframes bereits vor dem Effekt gefeuert hat (in ms) */
const ATTACH_FALLBACK_DELAY_MS = 4000;

/**
 * Liefert true, solange das Bunny-Video im übergebenen iframe läuft.
 *
 * Der Startwert ist true, sobald ein Player gerendert ist: Die Embeds der Seite
 * werden mit `autoplay=true` eingehängt, das Video läuft also bereits, bevor
 * sich player.js angebunden hat. Danach folgt der Wert den Events 'play',
 * 'pause' und 'ended'.
 *
 * Kann player.js nicht geladen oder der Player nicht angebunden werden,
 * bleibt der Wert true — im Zweifel gilt das Video als laufend, damit der
 * Aufrufer (z. B. eine Laufschrift) nicht mitten in der Wiedergabe wieder
 * losläuft.
 *
 * @param iframeRef Ref auf das Bunny-Embed-iframe
 * @param playerKey null, solange kein Player gerendert ist (liefert dann false).
 *   Wechselt der Wert, bindet sich der Hook an das dann aktuelle iframe neu an.
 */
export function useBunnyIsPlaying(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  playerKey: number | string | null
): boolean {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (playerKey === null) {
      setPlaying(false);
      return;
    }

    // Das Embed startet per autoplay — bis zum ersten Event gilt: läuft.
    setPlaying(true);

    const iframe = iframeRef.current;
    if (iframe === null) return;

    let cancelled = false;
    let player: PlayerJsPlayer | null = null;
    let fallbackTimeoutId: number | null = null;

    const handlePlay = () => setPlaying(true);
    const handleStop = () => setPlaying(false);

    const attach = async () => {
      // Idempotent: 'load'-Event und Timeout-Fallback können beide feuern.
      if (player !== null || cancelled) return;

      const ns = await loadPlayerJs();
      if (ns === null || cancelled || player !== null) return;

      try {
        player = new ns.Player(iframe);
        player.on('play', handlePlay);
        player.on('pause', handleStop);
        player.on('ended', handleStop);
      } catch {
        // player.js konnte sich nicht anbinden — der Wert bleibt auf true.
        player = null;
      }
    };

    iframe.addEventListener('load', attach);
    fallbackTimeoutId = window.setTimeout(attach, ATTACH_FALLBACK_DELAY_MS);

    return () => {
      cancelled = true;
      if (fallbackTimeoutId !== null) {
        window.clearTimeout(fallbackTimeoutId);
        fallbackTimeoutId = null;
      }
      iframe.removeEventListener('load', attach);
      if (player !== null) {
        try {
          player.off('play', handlePlay);
          player.off('pause', handleStop);
          player.off('ended', handleStop);
          player.destroy();
        } catch {
          // Player bereits zerstört — nichts zu tun.
        }
        player = null;
      }
    };
  }, [iframeRef, playerKey]);

  return playing;
}
